import React from 'react';
import styled from '@emotion/styled';
import { ComponentStyle, ProductComponentProps } from '../../types/types';
import { Config, StyleLibrary } from '@vm/omega-shared-component-webapp';
const { mqBreakpoints, pxToRem } = StyleLibrary;

import { SummaryHeader } from './SummaryHeader';
import { SummaryPoints } from './SummaryPoints';
import { ControlledAccordion } from './ControlledAccordion';

import { productComponentContainerPaddingStyle } from '../shared/styles';

const SummaryComponentContainer = styled.div(productComponentContainerPaddingStyle, {
  // z-index is 1 here to ensure the pattern from the Header Component appears infront of the backgorund but behind the content.
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
  '&.B': {
    // z-index is 1 here because this component doesn't allow the header pattern to overlap
    backgroundColor: 'var(--color-white)',
    position: 'relative',
    zIndex: 1,

    '& .table-wrapper': {
      overflowX: 'auto',

      [mqBreakpoints.desktop]: {
        overflowX: 'revert', // to prevent table clipping on desktop as we want to have it wider than paragraph
      },
    },

    '& table': {
      margin: `${pxToRem(32)} 0`,
      borderCollapse: 'collapse',
      textAlign: 'left',
      width: '100%',

      [mqBreakpoints.desktop]: {
        width: '70vw',
        maxWidth: pxToRem(1120),
      },

      '& tr': {
        borderWidth: 0,
        borderTopWidth: '1px',
        borderColor: 'var(--color-border)',
        borderStyle: 'solid',

        '&:first-of-type': {
          borderTopWidth: '2px',
        },
        '&:last-of-type': {
          borderBottomWidth: '1px',
        },
        padding: pxToRem(16),
      },

      '& th': {
        padding: pxToRem(16),
        fontWeight: 'var(--font-weight-medium)',
      },

      '& thead th': {
        fontSize: pxToRem(20),
        '&:not(:first-of-type)': {
          textAlign: 'center',
        },
      },

      '& tbody th': {
        fontSize: pxToRem(16),
      },

      '& td': {
        padding: pxToRem(16),
        textAlign: 'center',

        '&.tick': {
          color: 'var(--color-lime-darker)',
        },
        '&.cross': {
          color: 'var(--color-primary)',
        },
      },

      '& td.tick': {
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundImage: `url(${Config.get('image_cdn_base_url')}/ui/shared-component-webapp/lists/tick-lime-darker.svg)`,
      },

      '& tbody tr:nth-of-type(odd)': {
        backgroundColor: 'var(--color-light-grey)',
      },
    },
  },

  '&.C': {
    backgroundColor: 'var(--color-dark)',
    color: 'var(--color-body)',

    'h1, h2': {
      color: 'var(--color-secondary)',
    },
    p: {
      color: 'var(--color-white)',
    },
  },
});

const SummaryAccordionContainer = styled.div({
  maxWidth: 'var(--max-content-width)',
});

export const Summary: React.FC<ProductComponentProps> = props => {
  const { title, description, points, style } = props.productComponent;
  const componentIdx = props.index ?? 0;

  switch (style) {
    case ComponentStyle.B:
      return (
        <SummaryComponentContainer id={`mp-product-summary-${componentIdx}`} data-testid='mp-product-summary' className={style}>
          <SummaryHeader title={title} strapline={description} />
        </SummaryComponentContainer>
      );
    case ComponentStyle.C:
      return (
        <SummaryComponentContainer id={`mp-product-summary-${componentIdx}`} data-testid='mp-product-summary' className={style}>
          <SummaryHeader title={title} strapline={description} />
          <SummaryPoints points={points} />
        </SummaryComponentContainer>
      );
    default:
      return (
        <SummaryComponentContainer id={`mp-product-summary-${componentIdx}`} data-testid='mp-product-summary' className={style}>
          <SummaryHeader title={title} strapline={description} />
          <SummaryAccordionContainer data-testid='mp-product-summary-accordion'>
            <ControlledAccordion points={points} />
          </SummaryAccordionContainer>
        </SummaryComponentContainer>
      );
  }
};
